/**
 * Barre de recherche + filtres niveau / domaine des SKILLCARDS (app).
 * Les options viennent des cartes chargées (`cardLevelOptions`,
 * `cardDomainOptions` de @jeraisonne/db/cards) : jamais un filtre vide.
 */

import { Search, X } from 'lucide-react';
import {
  cardDomainOptions,
  cardLevelOptions,
  type CardFilterable,
  type CardFilters,
} from '@jeraisonne/db/cards';

const selectClass =
  'border-ink-200 text-ink-800 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border bg-white px-3 text-sm focus:outline-none';

export function SkillcardFilters({
  cards,
  value,
  onChange,
}: {
  cards: readonly CardFilterable[];
  value: CardFilters;
  onChange: (next: CardFilters) => void;
}) {
  const levels = cardLevelOptions(cards);
  const domains = cardDomainOptions(cards);
  return (
    <div className="space-y-2" role="search">
      <label className="relative block">
        <span className="sr-only">Rechercher une SKILLCARD</span>
        <Search
          className="text-ink-400 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
          aria-hidden
        />
        <input
          type="search"
          value={value.q ?? ''}
          onChange={(e) => onChange({ ...value, q: e.target.value })}
          placeholder="Rechercher une carte…"
          className="border-ink-200 text-ink-900 focus:border-primary-500 h-11 w-full rounded-xl border bg-white pl-9 pr-11 text-sm focus:outline-none"
        />
        {value.q ? (
          <button
            type="button"
            onClick={() => onChange({ ...value, q: '' })}
            aria-label="Effacer la recherche"
            className="text-ink-400 hover:text-ink-700 absolute right-0 top-0 flex h-11 w-11 items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </label>
      {/* Un filtre à une seule valeur ne sert à rien : on ne l'affiche pas. */}
      <div className="grid grid-cols-2 gap-2">
        {levels.length > 1 && (
          <label className="block">
            <span className="sr-only">Niveau</span>
            <select
              className={selectClass}
              value={value.level ?? ''}
              onChange={(e) => onChange({ ...value, level: e.target.value })}
              aria-label="Niveau"
            >
              <option value="">Tous les niveaux</option>
              {levels.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label} ({l.count})
                </option>
              ))}
            </select>
          </label>
        )}
        {domains.length > 1 && (
          <label className="block">
            <span className="sr-only">Domaine</span>
            <select
              className={selectClass}
              value={value.domain ?? ''}
              onChange={(e) => onChange({ ...value, domain: e.target.value })}
              aria-label="Domaine"
            >
              <option value="">Tous les domaines</option>
              {domains.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.value} ({d.count})
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
    </div>
  );
}
